import type { VideoFrame } from '../types';
import { VERTEX_SHADER, fragmentShaderFor, type ShaderId } from './shaders';
import { convertFrame } from './convert';

export interface WebGLRendererOptions {
  aspect?: number;
  integerScale?: boolean;
  shader?: ShaderId;
}

/**
 * WebGL によるフレーム描画。
 *
 * Canvas2D 版 (`CanvasRenderer`) と同じ役割・同じ外部インタフェースを持つ。
 * シェーダを掛けたいときだけこちらを使い、掛けないなら Canvas2D の方が軽い。
 *
 * WebGL2 ではなく WebGL1 を使うのは、古い iOS でも動かすため。
 * この用途では WebGL2 の利点がほぼ無い。
 */
export class WebGLRenderer {
  readonly canvas: HTMLCanvasElement;
  private gl: WebGLRenderingContext;
  private program: WebGLProgram | null = null;
  private texture: WebGLTexture | null = null;
  private buffer: WebGLBuffer | null = null;

  private uTex: WebGLUniformLocation | null = null;
  private uTexSize: WebGLUniformLocation | null = null;
  private uOutSize: WebGLUniformLocation | null = null;

  private width = 0;
  private height = 0;
  /** RGBA8888 に変換したフレーム。テクスチャへ直接転送する。 */
  private rgba = new Uint8Array(0);
  private rgba32 = new Uint32Array(0);

  private opts: WebGLRendererOptions;
  private container: HTMLElement | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private displayAspect: number | null = null;
  private shaderId: ShaderId;
  private lost = false;

  constructor(opts: WebGLRendererOptions = {}) {
    this.opts = opts;
    this.shaderId = opts.shader ?? 'none';

    this.canvas = document.createElement('canvas');
    this.canvas.width = 1;
    this.canvas.height = 1;

    const gl = this.canvas.getContext('webgl', {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      // 描画のたびにクリアされない方が扱いやすい
      preserveDrawingBuffer: true,
      powerPreference: 'low-power',
    });
    if (!gl) throw new WebGLUnavailableError();
    this.gl = gl;

    // GPU コンテキストはモバイルで普通に失われる。
    // 失われたまま描き続けると例外が出続けるのでフラグで止める。
    this.canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      this.lost = true;
    });
    this.canvas.addEventListener('webglcontextrestored', () => {
      this.lost = false;
      this.setupGl();
      this.allocTexture(this.width, this.height);
    });

    this.applyStyle();
    this.setupGl();
  }

  private applyStyle(): void {
    const s = this.canvas.style;
    s.display = 'block';
    s.backgroundColor = '#000';
    s.touchAction = 'none';
    s.userSelect = 'none';
    (s as CSSStyleDeclaration & { webkitUserSelect?: string }).webkitUserSelect = 'none';
  }

  /* ------------------------------------------------------------------ */
  /* GL のセットアップ                                                   */
  /* ------------------------------------------------------------------ */

  private compile(type: number, source: string): WebGLShader {
    const gl = this.gl;
    const shader = gl.createShader(type);
    if (!shader) throw new Error('failed to create shader');
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const log = gl.getShaderInfoLog(shader);
      gl.deleteShader(shader);
      throw new Error(`shader compile failed: ${log}`);
    }
    return shader;
  }

  private setupGl(): void {
    const gl = this.gl;

    if (this.program) gl.deleteProgram(this.program);

    const vs = this.compile(gl.VERTEX_SHADER, VERTEX_SHADER);
    const fs = this.compile(gl.FRAGMENT_SHADER, fragmentShaderFor(this.shaderId));

    const program = gl.createProgram();
    if (!program) throw new Error('failed to create program');
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    gl.deleteShader(vs);
    gl.deleteShader(fs);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const log = gl.getProgramInfoLog(program);
      gl.deleteProgram(program);
      throw new Error(`program link failed: ${log}`);
    }

    this.program = program;
    gl.useProgram(program);

    this.uTex = gl.getUniformLocation(program, 'uTex');
    this.uTexSize = gl.getUniformLocation(program, 'uTexSize');
    this.uOutSize = gl.getUniformLocation(program, 'uOutSize');

    // 画面全体を覆う三角形 1 枚。矩形 2 枚より頂点が少なく境目も出ない。
    if (!this.buffer) this.buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);

    const aPos = gl.getAttribLocation(program, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    gl.uniform1i(this.uTex, 0);
    gl.activeTexture(gl.TEXTURE0);
  }

  private allocTexture(w: number, h: number): void {
    if (w === 0 || h === 0) return;
    const gl = this.gl;

    if (this.texture) gl.deleteTexture(this.texture);
    this.texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.texture);

    // ドットをぼかさない
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    // 非 2 冪サイズを扱うので CLAMP 必須
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  }

  /* ------------------------------------------------------------------ */
  /* レイアウト（CanvasRenderer と同じ規則）                             */
  /* ------------------------------------------------------------------ */

  attachTo(container: HTMLElement): void {
    this.container = container;
    container.appendChild(this.canvas);
    this.resizeObserver = new ResizeObserver(() => this.layout());
    this.resizeObserver.observe(container);
    this.layout();
  }

  setDisplayAspect(aspect: number): void {
    if (this.displayAspect === aspect) return;
    this.displayAspect = aspect;
    this.layout();
  }

  setIntegerScale(on: boolean): void {
    if (this.opts.integerScale === on) return;
    this.opts.integerScale = on;
    this.layout();
  }

  setShader(id: ShaderId): void {
    if (this.shaderId === id) return;
    this.shaderId = id;
    if (!this.lost) this.setupGl();
  }

  private layout(): void {
    const container = this.container;
    if (!container) return;

    const style = getComputedStyle(container);
    const availW = container.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    const availH = container.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
    if (availW <= 0 || availH <= 0) return;

    const aspect = this.displayAspect ?? this.opts.aspect ?? (this.width || 4) / (this.height || 3);

    let w = availW;
    let h = w / aspect;
    if (h > availH) {
      h = availH;
      w = h * aspect;
    }

    if (this.opts.integerScale && this.height > 0) {
      const scale = Math.max(1, Math.floor(h / this.height));
      const snappedH = this.height * scale;
      if (snappedH <= availH) {
        h = snappedH;
        w = h * aspect;
      }
    }

    const cssW = Math.floor(w);
    const cssH = Math.floor(h);
    this.canvas.style.width = `${cssW}px`;
    this.canvas.style.height = `${cssH}px`;

    // シェーダの見た目は出力解像度に依存するので、
    // 描画バッファは CSS サイズに合わせる（DPR は上限を掛けて負荷を抑える）。
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const bw = Math.max(1, Math.round(cssW * dpr));
    const bh = Math.max(1, Math.round(cssH * dpr));
    if (this.canvas.width !== bw || this.canvas.height !== bh) {
      this.canvas.width = bw;
      this.canvas.height = bh;
    }
  }

  /* ------------------------------------------------------------------ */
  /* 描画                                                                */
  /* ------------------------------------------------------------------ */

  private ensureSize(w: number, h: number): void {
    if (this.width === w && this.height === h) return;
    this.width = w;
    this.height = h;
    this.rgba = new Uint8Array(w * h * 4);
    this.rgba32 = new Uint32Array(this.rgba.buffer);
    this.allocTexture(w, h);
    this.layout();
  }

  draw(frame: VideoFrame): void {
    if (this.lost) return;
    this.ensureSize(frame.width, frame.height);
    if (!this.program || !this.texture) return;

    convertFrame(frame, this.rgba32, this.rgba);

    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, frame.width, frame.height, gl.RGBA, gl.UNSIGNED_BYTE, this.rgba);

    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.uniform2f(this.uTexSize, frame.width, frame.height);
    gl.uniform2f(this.uOutSize, this.canvas.width, this.canvas.height);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  clear(): void {
    if (this.lost) return;
    const gl = this.gl;
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
  }

  async toBlob(type = 'image/png'): Promise<Blob | null> {
    return new Promise((resolve) => {
      this.canvas.toBlob((b) => resolve(b), type);
    });
  }

  dispose(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.container = null;

    const gl = this.gl;
    if (this.texture) gl.deleteTexture(this.texture);
    if (this.buffer) gl.deleteBuffer(this.buffer);
    if (this.program) gl.deleteProgram(this.program);
    // 明示的に開放しないとモバイルで context が枯れる
    gl.getExtension('WEBGL_lose_context')?.loseContext();

    this.canvas.remove();
  }
}

export class WebGLUnavailableError extends Error {
  constructor() {
    super('WebGL を利用できません');
    this.name = 'WebGLUnavailableError';
  }
}

